import { APIError } from 'models/errors';

export type Device = {
  id: string;
  type: string;
  attributes: {
    iprId: string;
    pnc: string;
    articleDesc: string;
    serialNumber: string;
    status: string;
    lastConnection: number;
    softwareUpdate: string;
    features: FeatureList;
    imageUrl: string;
    iccid?: string;
    hasPsks?: boolean;
    isTestDevice?: boolean;
    testDeviceDescription?: string;
    isFavoriteDevice: boolean;
  };
};

export interface DesiredData {
  resourceId: string;
  value: string;
  setBy: string;
}

export type DeviceShadow = {
  id: string;
  type: string;
  attributes: {
    features: DeviceShadowFeature[];
    rawData: string;
    desired?: DesiredData[];
  };
};

export type Range = {
  min?: number;
  max?: number;
  enums?: { description: string; key: string; value: number }[];
  type: string;
};

export type DeviceShadowFeature = {
  title: string;
  active: boolean;
  value?: string;
  children?: DeviceShadowFeature[];
  modified?: number;
  writable?: boolean;
  type?: string;
  range?: Range;
  desired?: DesiredValue;
  totalDesiredCount?: number;
};

export type DigitalTwin = {
  iprId: string;
  hid: string;
  serialNumber?: string;
  active: boolean;
  status: string;
  hasPsks: boolean;
  isBleModule: boolean;
  attachedTo: string;
  components?: {
    description: string;
    digitalTwin: DigitalTwin;
  }[];
};

export type DeviceMeta = {
  totalCount: number;
  onlyFavorites: boolean;
  lastEvaluatedId?: string;
};

export type DesiredValue = {
  resourceId: string;
  value: string;
  setBy?: string;
  failed?: number;
};

export interface DevicesResponse {
  data: Device[];
  meta: DeviceMeta;
  errors?: APIError[];
}
export interface SpecificationResponse {
  data: {
    attributes: JSON;
  };
  errors?: APIError[];
}

export interface DigitalTwinData {
  id: string;
  type: string;
  attributes: DigitalTwin;
  errors?: APIError[];
}

export interface DigitalTwinResponse {
  data: DigitalTwinData;
  errors?: APIError[];
}

export enum DeviceFilterAttribute {
  PNC = 'pnc',
  SerialNumber = 'serialNumber',
  UpdateStatus = 'updateStatus',
  IprId = 'iprId',
  HID = 'hid',
  ICCID = 'iccid',
  LastConnectionAfter = 'lastConnectionAfter',
  LastConnectionBefore = 'lastConnectionBefore',
  HappFirmwareVersion = 'happFirmwareVersion',
  TestDevice = 'testDevice',
}

export enum DeviceErrorFilterAttribute {
  Start = 'start',
  End = 'end',
}

export enum DeviceFamilyFilterAttribute {
  FamilyKey = 'familyKey',
  FamilyValue = 'familyValue',
  SelectedShadowFamily = 'selectedShadowFamily',
}

export enum DeviceFeatureFilterAttribute {
  FeatureKey = 'featureKey',
  FeatureValue = 'featureValue',
  FeatureSpecification = 'featureSpecification',
}

export type DeviceFilter = {
  attribute: DeviceFilterAttribute;
  value: string;
};

export type DevicesSuccess = {
  response: Device[];
  totalCount: number;
  lastEvaluatedId?: string;
  onlyFavorites: boolean;
  filters: DeviceFilter[] | null;
};
export type DeviceDetailsSuccess = {
  deviceInfo: Device;
  feature: string | null;
};

export type DeviceShadowSuccess = {
  deviceShadow: DeviceShadowFeature[];
  exportData: string;
  desired: DesiredData[];
};

export type DeviceUpdateSuccess = {
  isTestDevice: boolean;
  description: string;
};

export type FavoriteDeviceUpdateSuccess = {
  isFavoriteDevice: boolean;
  iprId: string;
};

export type DigitalTwinSuccess = {
  digitalTwin: DigitalTwin;
};

export type DeviceErrorsSuccess = {
  deviceErrors: DeviceErrorMap[];
};

export type ConnectivityState = {
  list: DeviceList;
  details: ConnectivityDetails;
};

export type DeviceList = {
  data: Device[] | null;
  totalCount: number;
  onlyFavorites: boolean;
  lastEvaluatedId: string | null;
  isLoading: boolean;
  error: APIError | null;
  filters: {
    active: boolean;
    data: DeviceFilter[] | null;
  };
};

export type ShadowResponse = {
  data: DeviceShadow;
  errors?: APIError[];
};

export interface DeviceUpdateResponse {
  data: {
    attributes: {
      isTestDevice: boolean;
      description: string;
    };
  };
  errors?: APIError[];
}

export interface FavoriteDeviceUpdateResponse {
  data: {
    id: string;
    type: string;
    attributes: {
      isFavoriteDevice: boolean;
    };
  };
  errors?: APIError[];
}

export type TableSoftwareHistory = {
  software: string;
  date: string;
  status: string;
};

export type ConnectivityDetails = {
  deviceInfo: Device | null;
  deviceShadow: {
    data: DeviceShadowFeature[] | null;
    exportData: string | null;
    isLoading: boolean;
    error: APIError | null;
  };
  digitalTwin: {
    data: DigitalTwin | null;
    isLoading: boolean;
    error: APIError | null;
  };
  deviceErrors: {
    data: DeviceErrorMap[] | null;
    isLoading: boolean;
    error: APIError | null;
  };
  annotations: {
    data: {
      systems: System[] | null;
      slsEnabled: boolean | null;
      adapter: string | null;
      instanceId: string | null;
    } | null;
    isLoading: boolean;
    error: APIError | null;
  };
  isLoading: boolean;
  error: APIError[] | null;
  update: {
    isLoading: boolean;
    error: APIError | null;
  };
  feature: string | null;
  desired: {
    isLoading: boolean;
    error: APIError | null;
    data: DesiredData[] | null;
  };
};

export type FeatureList = { [feature: string]: string };

export type DeviceError = {
  code: string;
  data?: {
    action: string;
    object: string;
    resource: string;
  };
  timestamp: string;
};

export type DeviceErrorMap = {
  code: string;
  deviceErrors: DeviceError[];
};

export type DeviceErrorsResponse = {
  data: {
    type: string;
    attributes: {
      errors: DeviceErrorMap[];
    };
  };
  errors?: APIError[];
};

export type AnnotationsSuccess = {
  data: {
    systems?: System[];
    slsEnabled?: boolean;
    adapter?: string;
    instanceId?: string;
  };
};

export type SpecificationSuccess = {
  data: JSON | null;
};
export type System = {
  system: string;
  user: string;
  accessLevel: string;
};

export type AnnotationsResponse = {
  data: {
    type: string;
    attributes: {
      systems?: System[];
      testDevice?: boolean;
      slsEnabled?: boolean;
      adapter?: string;
      instanceId?: string;
    };
  };
  errors?: APIError[];
};

export enum UpdateDeviceAttribute {
  Description = 'description',
  IsTestDevice = 'isTestDevice',
}
